<!-- ArticleLayout.vue - long-form pages (Datenschutz, Impressum, Über, Barrierefreiheit).
     Figma: "Content pages redesign" > Artikel. Title band, table of contents (side column on wide screens,
     collapsible box on phones), one readable text column, optional contact card. Route uses layout 'wide'. -->
<template>
  <article class="article">
    <header class="title-band bleed">
      <div class="band-inner">
        <p v-if="eyebrow" class="eyebrow">{{ eyebrow }}</p>
        <h1>{{ title }}</h1>
        <p v-if="asOf || readingMinutes" class="meta">
          <span v-if="asOf" class="chip">{{ asOf }}</span>
          <span v-if="readingMinutes">{{ $t('common.article.readingTime', { n: readingMinutes }) }}</span>
        </p>
        <p v-if="lead" class="lead">{{ lead }}</p>
      </div>
    </header>

    <div class="article-body">
      <nav v-if="sections.length > 1" class="toc" :aria-label="$t('common.article.toc')">
        <details class="toc-box" :open="wide">
          <summary>
            <span>{{ $t('common.article.toc') }}</span>
            <span class="toc-count">{{ sections.length }}</span>
          </summary>
          <ol>
            <li v-for="s in sections" :key="s.id">
              <a :href="'#' + s.id" :class="{ active: active === s.id }" @click="active = s.id">{{ s.title }}</a>
            </li>
          </ol>
        </details>
      </nav>
      <div class="article-text">
        <slot />
        <aside v-if="$slots.contact" class="contact-card">
          <slot name="contact" />
        </aside>
      </div>
    </div>
  </article>
</template>

<script>
export default {
  name: 'ArticleLayout',
  props: {
    eyebrow: { type: String, default: '' },
    title: { type: String, required: true },
    asOf: { type: String, default: '' },
    lead: { type: String, default: '' },
    readingMinutes: { type: Number, default: 0 },
    sections: { type: Array, default: () => [] }, // [{ id, title }] in reading order
  },
  data() {
    return { active: '', wide: typeof window !== 'undefined' && window.matchMedia('(min-width: 960px)').matches };
  },
  watch: {
    sections() { this.observe(); }, // sections can arrive later (About loads them from PocketBase)
  },
  mounted() { this.observe(); },
  methods: {
    // Highlight the section in view (only a hint, the links work without it)
    observe() {
      if (!('IntersectionObserver' in window)) return;
      if (this.observer) this.observer.disconnect();
      this.observer = new IntersectionObserver((entries) => {
        const hit = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (hit) this.active = hit.target.id;
      }, { rootMargin: '0px 0px -70% 0px' });
      this.$nextTick(() => this.sections.forEach((s) => { const el = document.getElementById(s.id); if (el) this.observer.observe(el); }));
    },
  },
  beforeUnmount() { if (this.observer) this.observer.disconnect(); },
};
</script>

<style scoped>
.article { display: flex; flex-direction: column; text-align: left; }
.title-band { background: var(--color-band); }
.band-inner { padding: 56px var(--page-inset, 16px) 48px; display: flex; flex-direction: column; gap: 12px; box-sizing: border-box; }
.eyebrow { margin: 0; font-family: var(--font-mono); font-size: 13px; font-weight: 500; color: var(--color-primary-text); }
h1 { margin: 0; font-size: clamp(27px, 5vw, 52px); line-height: 1.1; font-weight: 600; text-align: left; overflow-wrap: anywhere; hyphens: auto; }
.meta { margin: 0; display: flex; flex-wrap: wrap; align-items: center; gap: 8px; font-size: 13px; color: var(--color-secondary); }
.chip { font-family: var(--font-mono); font-size: 12px; padding: 4px 10px; border-radius: var(--radius-pill); background: var(--color-surface); border: 1px solid var(--color-border); }
.lead { margin: 4px 0 0; max-width: 720px; font-size: clamp(16px, 2vw, 19px); line-height: 1.55; color: var(--color-secondary); text-align: left; }

.article-body { display: grid; grid-template-columns: minmax(0, 1fr); gap: 24px; padding: 24px 0 48px; }
.toc-box { border: 1px solid var(--color-border); border-radius: var(--radius-lg); padding: 4px 16px; }
.toc-box summary { display: flex; justify-content: space-between; align-items: center; min-height: var(--touch-min); cursor: pointer; font-weight: 600; list-style: none; }
.toc-box summary::-webkit-details-marker { display: none; }
.toc-count { font-family: var(--font-mono); font-size: 12px; color: var(--color-secondary); }
.toc ol { list-style: none; margin: 0 0 12px; padding: 0; display: flex; flex-direction: column; gap: 2px; }
.toc a { display: block; padding: 8px 12px; border-radius: var(--radius-md); color: var(--color-secondary); text-decoration: none; font-size: 14px; }
.toc a:hover { color: var(--color-text); background: var(--color-band); }
.toc a.active { background: var(--color-subtle); color: var(--color-primary-text); font-weight: 600; }

.article-text { max-width: 720px; display: flex; flex-direction: column; gap: 36px; }
.article-text :deep(section) { scroll-margin-top: 24px; }
.article-text :deep(h2) { margin: 0 0 10px; font-size: clamp(20px, 2.4vw, 24px); font-weight: 600; text-align: left; }
.article-text :deep(p), .article-text :deep(li), .article-text :deep(address) { font-size: clamp(16px, 1.6vw, 17px); line-height: 1.65; text-align: left; }
.article-text :deep(p) { margin: 0 0 12px; }
.article-text :deep(a) { color: var(--color-primary-text); text-decoration: underline; }
.contact-card { display: flex; flex-wrap: wrap; align-items: center; gap: 16px; padding: 24px 28px; border-radius: var(--radius-xl); background: var(--color-subtle); }
.contact-card :deep(h2) { font-size: 18px; margin: 0 0 4px; }
.contact-card :deep(p) { margin: 0; font-size: 15px; color: var(--color-secondary); }

@media (min-width: 960px) {
  .band-inner { padding-top: 72px; padding-bottom: 64px; }
  .article-body { grid-template-columns: 260px minmax(0, 720px); gap: 80px; padding-top: 64px; padding-bottom: 80px; }
  .toc { position: sticky; top: 24px; align-self: start; }
  .toc-box { border: 0; padding: 0; }
  .toc-box summary { pointer-events: none; min-height: auto; margin-bottom: 8px; font-family: var(--font-mono); font-size: 12px; font-weight: 500; letter-spacing: 0.04em; text-transform: uppercase; color: var(--color-secondary); }
  .toc-count { display: none; }
}
</style>
